import { useState } from "react";
import { Field, Formik } from "formik";
import { Value } from "react-phone-number-input";

import { Button } from "Components/Button";
import Input from "Components/Form/Input";
import InputCurrency from "Components/Form/InputCurrency";
import { InputPhone } from "Components/Form/PhoneInput";
import { PaymentLinkButton } from "Components/Form/PaymentLinkButton";
import { SelectDocumentType } from "Components/Form/SelectDocumentType";
import { createCheckoutRemote } from "Requests/CheckoutRemote";
import { CheckoutRemotePayload } from "Requests/CheckoutRemote/types";
import { CopyToClipboard } from "Utils/Copy";
import { unmaskCPF } from "Utils/Masks";
import { createToast } from "Utils/toastFunc";

import { linkRemotoSchema } from "./schema";
import { Actions, FormContainer, PageContent } from "./styles";
import { LinkRemotoFormValues } from "./types";

const initialValues: LinkRemotoFormValues = {
  name: "",
  email: "",
  phone: "",
  document: "",
  document_type: "",
  amount: 0,
};

const getErrorMessage = (
  isTouched: boolean | undefined,
  error: string | undefined
) => {
  if (!isTouched || !error) return "";
  return error;
};

const sanitizePhone = (phone: string) => {
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("55") && digits.length > 11) {
    return digits.slice(2);
  }
  return digits;
};

const buildCheckoutPayload = (
  values: LinkRemotoFormValues
): CheckoutRemotePayload => {
  if (values.document_type !== "cpf" && values.document_type !== "passport") {
    throw new Error("Tipo de documento inválido");
  }

  return {
    name: values.name,
    email: values.email,
    phone: sanitizePhone(values.phone),
    document: unmaskCPF(values.document),
    document_type: values.document_type,
    amount: Number(values.amount),
  };
};

export const LinkRemoto = () => {
  const [paymentLink, setPaymentLink] = useState("");
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  const handleGenerateLink = async (values: LinkRemotoFormValues) => {
    setIsGeneratingLink(true);
    try {
      const response = await createCheckoutRemote(buildCheckoutPayload(values));
      setPaymentLink(response.payment_url);
    } catch (error: any) {
      const errors = error.response.data.messages;

      errors.forEach((message: any) => {
        createToast("error", message);
      });
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const copyLink = (link: string) => {
    CopyToClipboard({ message: "Link de pagamento copiado!", text: link });
  };

  return (
    <PageContent>
      <Formik
        initialValues={initialValues}
        onSubmit={handleGenerateLink}
        validationSchema={linkRemotoSchema}
      >
        {(formik) => {
          const {
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            resetForm,
          } = formik;

          const handleResetForm = () => {
            resetForm();
            setPaymentLink("");
          };

          return (
            <FormContainer>
              <h1>Checkout remoto</h1>

              <Field
                id="name"
                type="text"
                component={Input}
                label="Nome"
                placeholder="Nome"
                value={values.name}
                onChange={handleChange}
                error={getErrorMessage(touched.name, errors.name)}
              />

              <Field
                id="email"
                type="email"
                component={Input}
                label="E-mail"
                placeholder="E-mail"
                value={values.email}
                onChange={handleChange}
                error={getErrorMessage(touched.email, errors.email)}
              />

              <SelectDocumentType
                id="document_type"
                name="document_type"
                label="Tipo de documento"
                placeholder="Selecione"
                value={values.document_type}
                handleChange={(event) => {
                  setFieldValue("document_type", event.target.value);
                  setFieldValue("document", "");
                }}
                error={getErrorMessage(
                  touched.document_type,
                  errors.document_type
                )}
                disabled={false}
              />

              <Field
                id="document"
                type="text"
                component={Input}
                label="Documento"
                placeholder="Documento"
                value={values.document}
                onChange={handleChange}
                mask={values.document_type === "cpf" ? "999.999.999-99" : null}
                error={getErrorMessage(touched.document, errors.document)}
              />

              <Field
                id="phone"
                name="phone"
                component={InputPhone}
                label="Telefone"
                placeholder="Telefone"
                value={values.phone as Value}
                onChange={(phone: Value) => {
                  setFieldValue("phone", phone || "");
                }}
                error={getErrorMessage(touched.phone, errors.phone)}
              />

              <Field
                id="amount"
                name="amount"
                component={InputCurrency}
                label="Valor"
                placeholder="Valor"
                value={values.amount}
                onChange={(event: { target: { value: number } }) => {
                  setFieldValue("amount", event.target.value);
                  setPaymentLink("");
                }}
                error={getErrorMessage(!!touched.amount, errors.amount)}
              />

              <Actions>
                <Button
                  width="full"
                  type="submit"
                  minHeight="3em"
                  fontWeight="600"
                  loading={isGeneratingLink}
                  disabled={isGeneratingLink}
                >
                  Gerar link para pagamento
                </Button>

                <Button
                  width="full"
                  textButton
                  minHeight="3em"
                  fontWeight="600"
                  loading={isGeneratingLink}
                  disabled={isGeneratingLink}
                  onClick={handleResetForm}
                >
                  Resetar formulário
                </Button>
              </Actions>

              {paymentLink && (
                <PaymentLinkButton
                  placeholder="Link de pagamento"
                  value={paymentLink}
                  label="COPIAR LINK"
                  sendCopy={copyLink}
                  minHeight="3em"
                />
              )}
            </FormContainer>
          );
        }}
      </Formik>
    </PageContent>
  );
};

export default LinkRemoto;
